import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import axios from "axios";
import { Header } from "../ui/header";
import { Footer } from "../ui/footer";
import "../../css/blog.css";
import "../../css/header.css";
import "../../css/footer.css";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";

const BASE_URL = "https://admin.bookvenue.app/api";

const BlogList = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const blogsPerPage = 9;

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${BASE_URL}/get-all-blogs`);
        setBlogs(res.data.blogs || []);
      } catch (err) {
        console.error("Error fetching blogs:", err);
        setBlogs([]);
      } finally {
        setLoading(false);
      }
    };
    fetchBlogs();
  }, []);

  // Scroll to top when page changes
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [currentPage]);

  const indexOfLastBlog = currentPage * blogsPerPage;
  const indexOfFirstBlog = indexOfLastBlog - blogsPerPage;
  const currentBlogs = blogs.slice(indexOfFirstBlog, indexOfLastBlog);
  const totalPages = Math.ceil(blogs.length / blogsPerPage);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <>
      <Header />
      <Helmet>
        <title>
          Sports Venue Booking Blog | Cricket Ground & Turf Booking Guides
        </title>

        <meta
          name="description"
          content="Read expert sports venue booking guides, cricket ground booking tips, football turf booking insights, sports technology updates, and venue management strategies on Book Venue Blog."
        />

        <meta
          name="robots"
          content="index, follow"
        />

        <Link
          rel="canonical"
          to="https://bookvenue.app/blog"
        />
      </Helmet>

      <header className="contact-hero blog-hero">
        <div className="bv-container bv-container--wide contact-hero__inner">
          <nav className="contact-breadcrumb" aria-label="Breadcrumb">
            <Link to="/" className="contact-breadcrumb__link">
              <i className="fa-solid fa-house" aria-hidden="true" />
              Home
            </Link>
            <span className="contact-breadcrumb__sep" aria-hidden="true">
              <i className="fa-solid fa-chevron-right" />
            </span>
            <span className="contact-breadcrumb__current" aria-current="page">
              Blog
            </span>
          </nav>

          <div className="contact-hero__body">
            <div className="contact-hero__content">
              <span className="contact-hero__eyebrow">
                <i className="fa-solid fa-newspaper" aria-hidden="true" />
                Guides & inspiration
              </span>
              <h1 className="contact-hero__title">
                Sports venue booking <span className="gradientText">insights</span>
              </h1>
              <p className="contact-hero__lede">
                Explore guides, tips, and venue strategies to make booking cricket grounds,
                football turfs, badminton courts, and more simpler and smarter.
              </p>
            </div>
          </div>
        </div>
      </header>

      <section className="container mt-5 mb-5 blogcards">
        {loading ? (
          <div className="text-center">
            {/* <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div> */}
          </div>
        ) : currentBlogs.length === 0 ? (
          <p className="text-center text-muted">
            No blogs available at the moment.
          </p>
        ) : (
          <div className="row g-4">
            {currentBlogs.map((blog) => (
              <div
                key={blog.id || blog.slug}
                className="col-lg-4 col-md-6 col-sm-12"
              >
                <Link
                  to={`/blog/${blog.slug}`}
                  className="text-decoration-none text-dark"
                >
                  <div className="card h-100 blog-card border border-white shadow-sm">
                    {blog.featured_image ? (
                      <img
                        src={`https://admin.bookvenue.app/${blog.featured_image}`}
                        className="card-img-top"
                        alt={blog.title}
                        style={{
                          height: "200px",
                          objectFit: "cover",
                          width: "100%",
                        }}
                      />
                    ) : (
                      <div
                        className="card-img-top bg-secondary d-flex align-items-center justify-content-center"
                        style={{
                          height: "200px",
                          width: "100%",
                        }}
                      >
                        <span className="text-white">No Image</span>
                      </div>
                    )}
                    <div className="card-body">
                      <div className="d-flex align-items-center mb-3 text-muted small">
                        <i
                          className="bi bi-person-circle me-2"
                          style={{ fontSize: "1.2rem" }}
                        ></i>
                        <span className="me-2">Written by Admin</span>
                        <span>|</span>
                        <span className="ms-2">
                          {blog.created_at
                            ? new Date(blog.created_at).toLocaleDateString(
                                "en-US",
                                {
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                },
                              )
                            : "N/A"}
                        </span>
                      </div>
                      <h5 className="card-title mb-2">
                        <b>
                          {blog.title || "Untitled Blog"} | Sports Venue Booking
                          Guide
                        </b>
                      </h5>
                      <p className="card-text text-secondary">
                        {blog.description ? (
                          <>
                            {blog.description
                              .replace(/<[^>]+>/g, "")
                              .slice(0, 110)}
                            {"... "}
                            <span style={{ color: "#0d6efd" }}>Read more</span>
                          </>
                        ) : (
                          "Read insights on sports venue booking, turf availability, and online booking tips."
                        )}
                      </p>
                    </div>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <nav>
            <ul className="pagination justify-content-center mt-5">
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link green-page"
                  onClick={() => goToPage(currentPage - 1)}
                >
                  <IoIosArrowBack />
                </button>
              </li>
              {Array.from({ length: totalPages }, (_, index) => (
                <li
                  key={index}
                  className={`page-item ${currentPage === index + 1 ? "active" : ""}`}
                >
                  <button
                    onClick={() => goToPage(index + 1)}
                    className="page-link green-page"
                  >
                    {index + 1}
                  </button>
                </li>
              ))}
              <li
                className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
              >
                <button
                  className="page-link green-page"
                  onClick={() => goToPage(currentPage + 1)}
                >
                  <IoIosArrowForward />
                </button>
              </li>
            </ul>
          </nav>
        )}
      </section>

      <Footer />
    </>
  );
};

export default BlogList;
